import {
  LayoutDashboard,
  Home,
  Heart,
  EyeOff,
  Calendar,
  Bell,
  MessageSquare,
  User,
  Settings,
  HelpCircle,
  PlusCircle,
  List,
  Users,
  ClipboardList,
  BarChart3,
  Search,
  Flame,
  Mail,
  Webhook,
  Zap,
  BookOpen,
  
  MessageCircle,
  TrendingUp,
  Database,
  UserCheck,
  FileCheck,
  Calculator,
  Shield,
  Palette,
  ArrowRightLeft,
} from "lucide-react";
import FeatureTourModal, { type TourStep } from "@/components/ui/feature-tour-modal";

const agentSteps: TourStep[] = [
  {
    icon: LayoutDashboard,
    title: "Agent Dashboard",
    description:
      "Your command center. See active listings, recent client activity, engagement scores, and quick-action shortcuts — all at a glance.",
  },
  {
    icon: Home,
    title: "Browse Listings",
    description:
      "Search and filter the full listing inventory by price, beds, baths, location, and property type. Save searches and share results with clients instantly.",
  },
  {
    icon: List,
    title: "My Listings",
    description:
      "Manage every listing you've created — edit details, update photos, change status, or archive. Track views, saves, and tour requests for each property.",
  },
  {
    icon: PlusCircle,
    title: "Add Listing",
    description:
      "Create a new property listing with photos, details, pricing, and AI-generated descriptions. All listings live on the Privy network.",
  },
  {
    icon: Users,
    title: "My Clients",
    description:
      "View all your clients in one place — see engagement scores, activity levels, saved searches, and co-buyers. Invite new clients or import from CSV.",
  },
  {
    icon: Users,
    title: "My Team",
    description:
      "Invite assistants or fellow agents to your personal team. You can also find and invite agents directly from Agent Lookup.",
  },
  {
    icon: ClipboardList,
    title: "Inquiries / Property Leads",
    description:
      "Every lead captured from your listings appears here with contact info, the property they inquired about, and status tracking so nothing falls through the cracks.",
  },
  {
    icon: Palette,
    title: "Branding",
    description:
      "Customize your public agent profile — set your slug, bio, brokerage, and service areas. Generate a shareable link (e.g. /agent/your.name) that clients use to sign up and connect with you directly. Toggle auto-accept to control whether new signups are added automatically.",
  },
  {
    icon: Calendar,
    title: "Open Houses",
    description:
      "Schedule and manage open house events for your listings. Clients see these on the property detail page and can RSVP or request a private tour.",
  },
  {
    icon: TrendingUp,
    title: "Quick CMA",
    description:
      "Run a fast comparable market analysis by pulling nearby sold and active comps. Great for pricing discussions with sellers or preparing listing presentations.",
  },
  {
    icon: Flame,
    title: "Hot Sheets",
    description:
      "Set up automated watches for new listings, price drops, status changes, and back-on-market events in your target markets. Never miss an opportunity.",
  },
  {
    icon: Search,
    title: "Reverse Prospecting",
    description:
      "See which buyers' saved searches match your listing. Reach out to their agents or contact your own clients directly when there's a match.",
  },
  {
    icon: BarChart3,
    title: "Market Stats",
    description:
      "View aggregate market data — average prices, inventory levels, days on market, and trends — filtered by city, neighborhood, or zip code.",
  },
  {
    icon: BarChart3,
    title: "Listing Performance",
    description:
      "Deep-dive analytics for your listings: views, saves, tour requests, and engagement trends over time. Identify which properties are getting the most attention.",
  },
  {
    icon: Home,
    title: "Bring to Market",
    description:
      "Prepare off-market or coming-soon listings and control exactly when and how they go live. Perfect for building buzz before launch.",
  },
  {
    icon: Mail,
    title: "Email Templates",
    description:
      "Create and manage reusable email templates for client outreach, listing announcements, and follow-ups. Personalize with merge fields.",
  },
  {
    icon: MessageCircle,
    title: "Team Chat",
    description:
      "Internal messaging for your personal team. Discuss clients, share listing updates, and collaborate with your team members — all within the platform.",
  },
  {
    icon: Webhook,
    title: "Webhooks",
    description:
      "Set up webhook endpoints to receive real-time notifications when leads come in, tours are requested, or listings are saved by clients.",
  },
  {
    icon: Zap,
    title: "Zapier Integration",
    description:
      "Connect Privy to thousands of other apps through Zapier. Automate lead routing, CRM updates, email sequences, and more.",
  },
  {
    icon: BookOpen,
    title: "API Documentation",
    description:
      "Full API reference for developers. Use your API key to programmatically create listings, manage clients, and pull analytics data.",
  },
  {
    icon: Database,
    title: "GHL Integration",
    description:
      "Connect your GoHighLevel account to sync leads, contacts, and listing data bidirectionally. Keep your CRM and Privy in perfect sync.",
  },
  {
    icon: Database,
    title: "Data Import",
    description:
      "Bulk import clients, listings, or contacts from CSV files. Map columns to fields and preview before importing to avoid errors.",
  },
  {
    icon: UserCheck,
    title: "Access Requests",
    description:
      "Review and approve requests from potential clients who found your profile or listing page and want to connect with you.",
  },
  {
    icon: ArrowRightLeft,
    title: "Switch Requests",
    description:
      "Manage incoming client transfer requests. When a buyer requests to switch to you, review and accept or decline. Accepted transfers automatically reassign the client to you.",
  },
];

const clientSteps: TourStep[] = [
  {
    icon: LayoutDashboard,
    title: "Client Dashboard",
    description:
      "Your personalized home search hub. See recommended listings, saved properties, upcoming tours, and recent activity from your agent.",
  },
  {
    icon: Home,
    title: "Browse Listings",
    description:
      "Search the full property database with filters for price, beds, baths, location, and property type. Save listings you love or hide ones you don't.",
  },
  {
    icon: Heart,
    title: "Saved Listings",
    description:
      "All the properties you've saved in one place. Compare them side-by-side, leave comments, and get notified when prices change or new photos are added.",
  },
  {
    icon: EyeOff,
    title: "Hidden Listings",
    description:
      "Properties you've dismissed are kept here. You can always unhide them if you change your mind.",
  },
  {
    icon: Calendar,
    title: "My Tours",
    description:
      "View and manage your scheduled property tours. See confirmed times, request new tours, and leave feedback after each visit.",
  },
  {
    icon: ClipboardList,
    title: "Activity Feed",
    description:
      "A live stream of updates — price changes on saved listings, new matches from your searches, agent messages, and tour confirmations.",
  },
  {
    icon: BarChart3,
    title: "Activity Log",
    description:
      "A detailed history of every property you've viewed, saved, or interacted with. Useful for reviewing your search journey.",
  },
  {
    icon: User,
    title: "My Profile",
    description:
      "Update your budget range, preferred locations, and property requirements so your agent and the system can tailor recommendations.",
  },
  {
    icon: Bell,
    title: "Alert Preferences",
    description:
      "Control how and when you receive notifications — instant alerts, daily digests, or weekly summaries for new matches and price changes.",
  },
  {
    icon: ArrowRightLeft,
    title: "Switch Agent",
    description:
      "Found a different agent you'd prefer? Use Agent Lookup to search for agents and request a transfer. The new agent must accept before the switch takes effect.",
  },
];

const adminSteps: TourStep[] = [
  {
    icon: Shield,
    title: "Admin Panel",
    description:
      "Full platform oversight — manage users, roles, settings, and system configuration. Monitor platform health and user activity.",
  },
  {
    icon: BarChart3,
    title: "Admin Analytics",
    description:
      "Platform-wide metrics: total users, listings, engagement rates, and growth trends. Drill down by time period or user segment.",
  },
  {
    icon: UserCheck,
    title: "Agent Approval",
    description:
      "Review and approve new agent registrations. Verify license numbers and brokerage affiliations before granting access.",
  },
  {
    icon: FileCheck,
    title: "Listing Moderation",
    description:
      "Review flagged or pending listings. Approve, reject, or request changes to ensure listing quality and compliance.",
  },
  {
    icon: Database,
    title: "MLS Admin",
    description:
      "Manage MLS feed connections, sync schedules, and data mappings. Monitor feed health and resolve sync issues.",
  },
];

const commonSteps: TourStep[] = [
  {
    icon: Search,
    title: "Agent Lookup",
    description:
      "Search for agents by name across the platform. Clients can request to switch agents, agents can invite others to their team, and everyone can start a direct chat.",
  },
  {
    icon: Bell,
    title: "Notifications",
    description:
      "All your alerts in one place — new messages, listing updates, tour confirmations, and system announcements. Mark as read or take action directly.",
  },
  {
    icon: MessageSquare,
    title: "Chat",
    description:
      "Direct messaging between agents and clients. Share listings in conversation, attach files, and keep all communication in one secure thread.",
  },
  {
    icon: Calculator,
    title: "Mortgage Calculator",
    description:
      "Estimate monthly payments with adjustable inputs for price, down payment, interest rate, taxes, and insurance. Great for quick affordability checks.",
  },
  {
    icon: Settings,
    title: "Profile Settings",
    description:
      "Update your name, email, phone, avatar, and notification preferences. Manage your account security and connected integrations.",
  },
  {
    icon: HelpCircle,
    title: "Help Center",
    description:
      "Find answers to common questions, platform guides, and contact support. Everything you need to get the most out of Privy.",
  },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role?: string | null;
}

export default function PrivyFeatureTour({ open, onOpenChange, role }: Props) {
  let steps: TourStep[] = [];

  if (role === "agent" || role === "admin") {
    steps = [...agentSteps];
  }
  if (role === "client" || role === "co_buyer") {
    steps = [...clientSteps];
  }
  if (role === "admin" || role === "mls_admin") {
    steps = [...steps, ...adminSteps];
  }

  steps = [...steps, ...commonSteps];

  return <FeatureTourModal open={open} onOpenChange={onOpenChange} steps={steps} />;
}
